import type { FormType } from '@/hooks/types/aiFieldGeneration'
import type { GenerateMetadataParams, GenerateMetadataResponse } from '@/services/api/api.types'

// Configuration for AI-enhanced fields across different form types
export type AIFieldConfig = {
  formField: string
  autoGenerate?: boolean
} & (
  | {
      // Standard metadata generation
      type: 'metadata'
      apiParamField: GenerateMetadataParams['field']
      apiResponseField: keyof GenerateMetadataResponse['generated'][0]
    }
  | {
      // Specialized endpoints
      type: 'specialized'
      endpoint:
        | 'generateDemographic'
        | 'generateObjectives'
        | 'generateOutline'
        | 'generateFAQs'
        | 'generateVideoMetadata'
        | 'generateImageMetadata'
      responseField: 'demographic' | 'objectives' | 'outline' | 'questions' | 'title' | 'description'
    }
  | {
      // New unified generation endpoint
      type: 'generate'
      systemPromptTemplateId: string
      additionalParams?: Record<string, any>
    }
)

// Content Module field configurations
export const CM_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  name: {
    formField: 'name',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_title',
  },
  shortDescription: {
    formField: 'shortDescription',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_description',
  },
  introduction: {
    formField: 'introduction',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_introduction',
  },
  demographic: {
    formField: 'demographic',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_target_audience',
  },
  // sectionObjectives: {
  //   formField: 'sectionObjectives',
  //   type: 'generate',
  //   systemPromptTemplateId: 'generate_cm_objectives',
  // },
  outline: {
    formField: 'outline',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_outline',
  },
  faqs: {
    formField: 'faqs',
    type: 'specialized',
    endpoint: 'generateFAQs',
    responseField: 'questions',
    autoGenerate: true,
  },
  faqSingle: {
    formField: 'faqSingle',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_faq',
    additionalParams: { faq_list: 'faqs' },
  },
}

// Step field configurations
export const STEP_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  name: {
    formField: 'name',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_file_title',
  },
  shortDescription: {
    formField: 'shortDescription',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_file_description',
  },
  faqs: {
    formField: 'faqs',
    type: 'specialized',
    endpoint: 'generateFAQs',
    responseField: 'questions',
    autoGenerate: true,
  },
  faqSingle: {
    formField: 'faqSingle',
    type: 'generate',
    systemPromptTemplateId: 'generate_cm_faq',
    additionalParams: { faq_list: 'faqs' },
  },
}

// Resource field configurations
export const RESOURCE_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  title: {
    formField: 'title',
    type: 'metadata',
    apiParamField: 'title',
    apiResponseField: 'title',
  },
  description: {
    formField: 'description',
    type: 'metadata',
    apiParamField: 'description',
    apiResponseField: 'description',
  },
  // Note: FAQs are not supported for resources due to API limitations
}

// Media-specific resource field configurations
export const VIDEO_RESOURCE_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  title: {
    formField: 'title',
    type: 'specialized',
    endpoint: 'generateVideoMetadata',
    responseField: 'title',
  },
  description: {
    formField: 'description',
    type: 'specialized',
    endpoint: 'generateVideoMetadata',
    responseField: 'description',
  },
}

export const IMAGE_RESOURCE_FIELD_CONFIGS: Record<string, AIFieldConfig> = {
  title: {
    formField: 'title',
    type: 'specialized',
    endpoint: 'generateImageMetadata',
    responseField: 'title',
  },
  description: {
    formField: 'description',
    type: 'specialized',
    endpoint: 'generateImageMetadata',
    responseField: 'description',
  },
}

// Helper function to get field configs by type
export function getFieldConfigs(type: FormType): Record<string, AIFieldConfig> {
  switch (type) {
    case 'cm':
      return CM_FIELD_CONFIGS
    case 'step':
      return STEP_FIELD_CONFIGS
    case 'resource':
      return RESOURCE_FIELD_CONFIGS
    default:
      return {}
  }
}

// Helper function to generate a specific field using config
export function getGenerateFieldOptions(
  type: FormType,
  fieldName: string,
): (AIFieldConfig & { formType: FormType }) | null {
  const configs = getFieldConfigs(type)
  const config = configs[fieldName]

  if (!config) {
    console.warn(`No AI field config found for ${type}.${fieldName}`)
    return null
  }

  return {
    formType: type,
    ...config,
  }
}

// Helper function to get all auto-generation configs for a form type
export function getAutoGenerationConfigs(type: FormType): Array<AIFieldConfig & { formType: FormType }> {
  const configs = getFieldConfigs(type)
  return Object.values(configs)
    .filter(config => config.autoGenerate !== false)
    .map(config => ({ formType: type, ...config }))
}

// Helper function to get resource field configs based on resource type
export function getResourceFieldConfigByType(
  resourceType: 'text' | 'url' | 'video' | 'image',
  fieldName: string,
): AIFieldConfig | null {
  let configs: Record<string, AIFieldConfig>

  switch (resourceType) {
    case 'video':
      configs = VIDEO_RESOURCE_FIELD_CONFIGS
      break
    case 'image':
      configs = IMAGE_RESOURCE_FIELD_CONFIGS
      break
    case 'text':
    case 'url':
    default:
      configs = RESOURCE_FIELD_CONFIGS
      break
  }

  return configs[fieldName] || null
}
